// Shared token-pill machinery for contenteditable token editing.
//
// A token is written in a template as `{{ path (: modifier (: args)?)? }}`.
// Inside a contenteditable surface each token is inflated into a non-editable
// atomic pill carrying the inner form in data-token, so the raw template
// round-trips verbatim. Shared by the standalone HtmlTokenEditor and the
// in-place contract document editor.
import {
  TokenField,
  TOKEN_REGEX,
  labelForToken,
  parseToken,
  buildTokenInner,
  defaultModifier,
  modifiersFor,
} from '../htmlTokenSchema';

export const PILL_STYLE =
  'display:inline-flex;align-items:center;background-color:#e3f2fd;color:#0d47a1;border:1px solid #90caf9;border-radius:4px;padding:2px 6px;margin:0 4px;font-size:0.8rem;vertical-align:middle;';
// Block variant of the pill for tokens that don't flow with the surrounding
// text (the `table` modifier): full width, a couple of lines tall.
export const TABLE_PILL_STYLE =
  'display:flex;align-items:center;box-sizing:border-box;width:100%;min-height:3.2em;background-color:#e3f2fd;color:#0d47a1;border:1px solid #90caf9;border-radius:4px;padding:2px 8px;margin:8px 0;font-size:0.8rem;white-space:nowrap;';

// Escapes raw characters but intentionally leaves structural formatting tags intact
// when rendering the template wrapper down to working HTML.
export const escapeHtml = (text: string) => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Renders a token's inner form as a non-editable pill. The inner form (not a fixed
// id) is stored in data-token so it round-trips back to the raw template verbatim.
export const generateTokenHtml = (inner: string, fields: TokenField[], fresh = false): string => {
  const label = labelForToken(inner, fields);
  const freshAttr = fresh ? ' data-fresh="true"' : '';
  // `table` tokens render as a full-width block, not an inline pill, so it's
  // clear they don't flow with the surrounding text.
  const isTable = parseToken(inner).modifier === 'table';
  const tag = isTable ? 'div' : 'span';
  return `<${tag} contenteditable="false" class="atomic-token" data-token="${escapeHtml(
    inner
  )}"${freshAttr} style="${isTable ? TABLE_PILL_STYLE : PILL_STYLE}">${escapeHtml(label)}</${tag}>`;
};

// Maps a raw template string to working HTML, inflating each {{...}} token into a pill.
// The token inner is normalized (trimmed path/modifier, canonical form) so the
// pill round-trips back to a canonical {{...}} token.
export const templateToHtml = (template: string, fields: TokenField[]): string => {
  if (!template) return '';
  return template.replace(TOKEN_REGEX, (match) => {
    const rawInner = match.slice(2, -2);
    const { path, modifier, args } = parseToken(rawInner);
    return generateTokenHtml(buildTokenInner(path, modifier, args), fields);
  });
};

// Reverse translation: runtime pill DOM back into raw {{...}} template text.
export const htmlToTemplate = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const tokens = tempDiv.querySelectorAll('.atomic-token');
  tokens.forEach((token) => {
    const inner = token.getAttribute('data-token');
    if (inner) {
      token.parentNode?.replaceChild(document.createTextNode(`{{${inner}}}`), token);
    }
  });

  const allowedTags = ['B', 'I', 'S', 'STRIKE', 'U', 'MARK', 'UL', 'OL', 'LI', 'BR', 'DIV', 'P'];
  const sanitizeNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (!allowedTags.includes(el.tagName)) {
        const textNode = document.createTextNode(el.innerText || '');
        el.parentNode?.replaceChild(textNode, el);
      } else {
        el.removeAttribute('style');
        el.removeAttribute('onclick');
        el.childNodes.forEach(sanitizeNode);
      }
    }
  };

  tempDiv.childNodes.forEach(sanitizeNode);
  return tempDiv.innerHTML;
};

// Builds the default token inner form for a field, used on insert before the user
// is prompted to refine it (date->full, money->currency, collection->first field comma-joined).
// Collections offering the `images` modifier (artwork) default to `path:images` so a
// template can ship the selection and the campaign editor adjusts the args in place.
export const defaultInner = (field: TokenField): string => {
  if (field.type === 'collection') {
    if (modifiersFor(field).includes('images')) return buildTokenInner(field.path, 'images');
    const first = field.itemFields?.[0];
    if (first) return buildTokenInner(`${field.path}.${first.path}`, 'comma');
    return buildTokenInner(field.path, 'comma');
  }
  const mod = defaultModifier(field);
  return buildTokenInner(field.path, mod || undefined);
};

// Collapses the caret to sit directly after a token pill, so typing continues
// right where the token was inserted.
export const placeCaretAfterToken = (el: HTMLElement) => {
  const range = document.createRange();
  range.setStartAfter(el);
  range.collapse(true);
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
};

// Chip-style selection highlight for a token pill. The pill carries an inline
// style, which outranks ordinary rules, so these carry `!important`.
export const TOKEN_SELECTED_STYLE = `
.atomic-token.token-selected {
  background-color: #bbdefb !important;
  border-color: #0d47a1 !important;
  box-shadow: 0 0 0 1px #0d47a1;
}
`;

// Resolves a collapsed caret Range at the given viewport point, or null when
// the browser cannot map the point into editable content.
export const caretRangeFromPoint = (x: number, y: number): Range | null => {
  const doc = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof doc.caretRangeFromPoint === 'function') return doc.caretRangeFromPoint(x, y);
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(x, y);
    if (!pos) return null;
    const range = document.createRange();
    range.setStart(pos.offsetNode, pos.offset);
    range.collapse(true);
    return range;
  }
  return null;
};
